/**
 * My week, and the whole team's week underneath it.
 *
 * The order is deliberate: a person opens this to answer "when am I on", so
 * their own shifts are big cards at the top, and the team view is a second
 * section they scroll to. The team view carries no money at all, which is not
 * an oversight: the roster response omits cost for staff by contract, and this
 * screen never derives it either.
 */
import { useMemo, useState } from 'react';
import { Link } from 'wouter';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Clock3, Hand, MapPin, Undo2, Users } from 'lucide-react';
import { api, ApiError, qk, type Shift } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import {
  DAY_NAMES,
  addDays,
  dayIndex,
  fmtDateRange,
  fmtDayMonth,
  fmtDuration,
  fmtTimeRange,
  shiftMinutes,
  today,
  weekDates,
  weekStart,
  type IsoDate,
} from '../../lib/time.ts';
import { Avatar, EmptyState, ErrorState, Field, LoadingBlock, Spinner, colourTokens } from '../../components/Bits.tsx';
import { Modal, OutcomeModal } from '../../components/Overlay.tsx';

export default function MyWeekPage() {
  const { tenant, me } = useSession();
  const slug = tenant?.tenantSlug ?? '';
  const [week, setWeek] = useState<IsoDate>(() => weekStart(today()));
  const [showTeam, setShowTeam] = useState(false);
  const qc = useQueryClient();

  // "I cannot work this" flow. Deliberately two taps with a reason in between:
  // it is a message to a manager who has to find somebody else, not a button
  // that removes you from the roster.
  const [coverFor, setCoverFor] = useState<Shift | null>(null);
  const [reason, setReason] = useState('');
  const [outcome, setOutcome] = useState<{ ok: boolean; text: string } | null>(null);

  const query = useQuery({
    queryKey: qk.roster(slug, week),
    queryFn: () => api.roster(slug, week),
    enabled: slug !== '',
  });

  // How many shifts are going spare, so the person who just gave one up can see
  // the other side of the same system without hunting for it.
  const spare = useQuery({
    queryKey: qk.availableShifts(slug),
    queryFn: () => api.availableShifts(slug),
    enabled: slug !== '',
  });

  const cover = useMutation({
    mutationFn: (v: { shift: Shift; reason: string }) =>
      v.shift.needsCover
        ? api.cancelCover(slug, v.shift.id)
        : api.requestCover(slug, v.shift.id, v.reason),
    onSuccess: (res, v) => {
      void qc.invalidateQueries({ queryKey: qk.roster(slug, week) });
      void qc.invalidateQueries({ queryKey: qk.availableShifts(slug) });
      setCoverFor(null);
      setReason('');
      setOutcome({
        ok: true,
        text: v.shift.needsCover
          ? 'You are back on that shift and your manager has been told.'
          : (res as { message?: string }).message ??
            'Your manager has been told. You are still on the roster for that shift until they arrange cover, so speak to them as well.',
      });
    },
    onError: (e) => {
      setCoverFor(null);
      setOutcome({
        ok: false,
        text: e instanceof ApiError ? e.message : 'Could not send that. Try again in a moment.',
      });
    },
  });

  const dates = useMemo(() => weekDates(week), [week]);
  const data = query.data;
  const myId = me?.user.id;

  const areaName = useMemo(() => {
    const m = new Map<string, { name: string; colour: string | null }>();
    for (const a of data?.areas ?? []) m.set(a.id, { name: a.name, colour: a.colour });
    return m;
  }, [data?.areas]);

  const staffName = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of data?.staff ?? []) m.set(s.userId, s.name);
    return m;
  }, [data?.staff]);

  // Published only. A draft shift is the boss still thinking, and showing it
  // would have people turning up to a shift that was never real.
  const published = useMemo(
    () => (data?.shifts ?? []).filter((s) => s.status === 'published'),
    [data?.shifts],
  );
  const mine = useMemo(
    () => published.filter((s) => s.userId === myId).sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime)),
    [published, myId],
  );
  const myMinutes = mine.reduce((t, s) => t + shiftMinutes(s.startTime, s.endTime, s.breakMinutes), 0);

  const byDate = useMemo(() => {
    const m = new Map<string, Shift[]>();
    for (const s of published) {
      const list = m.get(s.date);
      if (list) list.push(s);
      else m.set(s.date, [s]);
    }
    for (const list of m.values()) list.sort((a, b) => a.startTime.localeCompare(b.startTime));
    return m;
  }, [published]);

  if (query.isLoading) return <LoadingBlock label="Loading your week" />;
  if (query.isError) return <ErrorState error={query.error} what="your week" onRetry={() => void query.refetch()} />;

  return (
    <div className="px-4 py-4">
      <header className="mb-4 flex items-center gap-2">
        <button
          type="button"
          className="btn btn-icon"
          onClick={() => setWeek(addDays(week, -7))}
          title="Previous week"
          aria-label="Previous week"
        >
          <ChevronLeft size={16} aria-hidden="true" />
        </button>
        <div className="min-w-0 flex-1 text-center">
          <h1 className="truncate text-[15px] font-extrabold leading-tight text-ink">
            {fmtDateRange(week, addDays(week, 6))}
          </h1>
          <p className="text-[12px] leading-tight text-muted">
            {mine.length === 0
              ? 'No shifts for you this week'
              : `${mine.length} ${mine.length === 1 ? 'shift' : 'shifts'}, ${fmtDuration(myMinutes)}`}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-icon"
          onClick={() => setWeek(addDays(week, 7))}
          title="Next week"
          aria-label="Next week"
        >
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      </header>

      {mine.length === 0 ? (
        <EmptyState
          icon={<Clock3 size={20} aria-hidden="true" />}
          title="Nothing rostered for you"
          body="When your manager publishes this week, your shifts appear here and your phone dings."
        />
      ) : (
        <div className="space-y-2.5">
          {mine.map((s, i) => {
            const area = s.areaId ? areaName.get(s.areaId) : null;
            const tokens = colourTokens(area?.colour ?? null);
            const isToday = s.date === today();
            return (
              <article
                key={s.id}
                className="card card-lift anim-rise press flex items-stretch overflow-hidden"
                style={{
                  animationDelay: `${i * 45}ms`,
                  borderColor: isToday ? 'var(--sp-accent)' : undefined,
                  // Today's card sits proud of the others: a soft halo in the
                  // accent, on top of the lift the rest already carry.
                  boxShadow: isToday ? '0 0 0 3px var(--sp-accent-soft), var(--sp-shadow-2)' : undefined,
                }}
              >
                <div
                  className="flex w-[70px] shrink-0 flex-col items-center justify-center py-3"
                  style={{ background: tokens.soft, borderRight: `3px solid ${tokens.solid}` }}
                >
                  <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: tokens.solid }}>
                    {DAY_NAMES[dayIndex(s.date)]?.slice(0, 3)}
                  </span>
                  <span className="text-[19px] font-extrabold leading-tight text-ink">{Number(s.date.slice(8))}</span>
                  <span className="text-[10.5px] text-muted">{fmtDayMonth(s.date).split(' ')[1]}</span>
                </div>
                <div className="min-w-0 flex-1 px-3.5 py-3">
                  <div className="flex items-center gap-2">
                    <span className="tabular text-[16px] font-extrabold leading-tight text-ink">
                      {fmtTimeRange(s.startTime, s.endTime)}
                    </span>
                    {isToday ? <span className="chip chip-accent">Today</span> : null}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 text-[12.5px] text-muted">
                    <span className="tabular">{fmtDuration(shiftMinutes(s.startTime, s.endTime, s.breakMinutes))}</span>
                    {s.breakMinutes > 0 ? <span>{s.breakMinutes}m break</span> : null}
                    {area ? (
                      <span className="flex items-center gap-1">
                        <MapPin size={12} aria-hidden="true" />
                        {area.name}
                      </span>
                    ) : null}
                  </div>
                  {s.notes ? <p className="mt-1.5 text-[12.5px] leading-snug text-ink">{s.notes}</p> : null}
                  {s.needsCover ? (
                    <p
                      className="mt-2 rounded-card px-2.5 py-1.5 text-[12px] leading-snug"
                      style={{
                        background: 'var(--sp-warn-soft)',
                        border: '1px solid var(--sp-warn-border)',
                        color: 'var(--sp-text)',
                      }}
                    >
                      Your manager knows you cannot work this. Until they put somebody else on it, this
                      shift is still yours.
                    </p>
                  ) : null}
                  {s.date >= today() ? (
                    <button
                      type="button"
                      className="btn btn-sm mt-2.5"
                      onClick={() => {
                        setReason('');
                        if (s.needsCover) cover.mutate({ shift: s, reason: '' });
                        else setCoverFor(s);
                      }}
                      disabled={cover.isPending}
                    >
                      {s.needsCover ? (
                        <>
                          <Undo2 size={14} aria-hidden="true" />
                          Actually, I can work it
                        </>
                      ) : (
                        <>
                          <Hand size={14} aria-hidden="true" />
                          I cannot work this
                        </>
                      )}
                    </button>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {(spare.data?.items.length ?? 0) > 0 ? (
        <Link
          href="/cover"
          className="card card-lift row-link press mt-5 flex items-center gap-3 px-3.5 py-3"
          style={{ borderColor: 'var(--sp-warn-border)', background: 'var(--sp-warn-soft)' }}
        >
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center"
            style={{
              borderRadius: 'var(--sp-radius)',
              background: 'var(--sp-surface)',
              color: 'var(--sp-warn-strong)',
            }}
            aria-hidden="true"
          >
            <Hand size={17} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[14px] font-bold text-ink">
              {spare.data?.items.length === 1
                ? '1 shift is up for grabs'
                : `${spare.data?.items.length} shifts are up for grabs`}
            </span>
            <span className="block text-[12.5px] text-muted">Pick up extra hours if you want them.</span>
          </span>
          <ChevronRight size={16} aria-hidden="true" className="row-chev shrink-0 text-faint" />
        </Link>
      ) : null}

      <section className="mt-6">
        <button
          type="button"
          className="btn w-full justify-between"
          onClick={() => setShowTeam((v) => !v)}
          aria-expanded={showTeam}
        >
          <span className="flex items-center gap-2">
            <Users size={15} aria-hidden="true" />
            Everyone this week
          </span>
          <ChevronRight
            size={16}
            aria-hidden="true"
            style={{
              transform: showTeam ? 'rotate(90deg)' : 'none',
              transition: 'transform 220ms var(--sp-ease-out)',
            }}
          />
        </button>

        {showTeam ? (
          <div className="mt-3 space-y-3">
            {dates.map((d) => {
              const list = byDate.get(d) ?? [];
              return (
                <div key={d} className="card overflow-hidden">
                  <div
                    className="flex items-baseline gap-2 px-3.5 py-2"
                    style={{
                      background: d === today() ? 'var(--sp-accent-soft)' : 'var(--sp-surface-2)',
                      borderBottom: '1px solid var(--sp-hairline)',
                    }}
                  >
                    <span className="text-[13px] font-bold text-ink">{DAY_NAMES[dayIndex(d)]}</span>
                    <span className="text-[12px] text-muted">{fmtDayMonth(d)}</span>
                    <span className="flex-1" />
                    <span className="text-[11.5px] text-faint">
                      {list.length === 0 ? 'Nobody on' : `${list.length} on`}
                    </span>
                  </div>
                  {list.length === 0 ? null : (
                    <ul>
                      {list.map((s) => (
                        <li key={s.id} className="flex items-center gap-2.5 px-3.5 py-2">
                          <Avatar name={s.userId ? (staffName.get(s.userId) ?? '?') : 'Open'} size={26} />
                          <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink">
                            {s.userId ? (staffName.get(s.userId) ?? 'Team member') : 'Open shift'}
                          </span>
                          {s.areaId ? (
                            <span className="chip chip-neutral">{areaName.get(s.areaId)?.name ?? 'Area'}</span>
                          ) : null}
                          <span className="tabular shrink-0 text-[12.5px] text-muted">
                            {fmtTimeRange(s.startTime, s.endTime)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}
      </section>

      <Modal
        open={coverFor !== null}
        onClose={() => setCoverFor(null)}
        title="Tell your manager you cannot work this"
        subtitle={
          coverFor
            ? `${fmtDayMonth(coverFor.date)}, ${fmtTimeRange(coverFor.startTime, coverFor.endTime)}`
            : undefined
        }
        footer={
          <>
            <button type="button" className="btn" onClick={() => setCoverFor(null)}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={cover.isPending}
              onClick={() => coverFor && cover.mutate({ shift: coverFor, reason: reason.trim() })}
            >
              {cover.isPending ? <Spinner size={15} label="Sending" /> : 'Send to my manager'}
            </button>
          </>
        }
      >
        <p className="mb-4 text-[13.5px] leading-relaxed text-muted">
          This does not take you off the roster. Your manager gets an alert straight away and the shift is
          offered to the rest of the team. You stay on it until somebody else is put on.
        </p>
        <Field
          label="Why, briefly"
          hint="Optional, but it helps your manager work out how urgent this is."
          id="cover-reason"
        >
          <input
            id="cover-reason"
            className="field"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Family thing, exam, unwell"
            maxLength={200}
          />
        </Field>
      </Modal>

      <OutcomeModal
        open={outcome !== null}
        onClose={() => setOutcome(null)}
        ok={outcome?.ok ?? true}
        title={outcome?.ok ? 'Sent' : 'That did not send'}
        message={outcome?.text ?? ''}
      />
    </div>
  );
}
